import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import { getTdsDetails, getPolymerFamily } from '../utils/tdsData';
import './TdsPage.css';

const TdsDetailPage = () => {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [gradeItem, setGradeItem] = useState(null);
  const [tdsData, setTdsData] = useState(null);
  const [error, setError] = useState(null);

  // Load TDS data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch grades list to find the matching code
        const response = await apiService.getPolymerLiveRate();
        let matchedGrade = null;

        if (response && response.success && Array.isArray(response.data)) {
          matchedGrade = response.data.find(
            (item) => (item.code_name || item.code || '').trim().toUpperCase() === String(code).trim().toUpperCase()
          );
        }

        // Fallback: If not found in live rates API (or if API fails), construct details dynamically
        if (!matchedGrade) {
          console.warn(`Grade code "${code}" not found in live rates. Constructing dynamic model.`);
          
          // Deduce polymer type from code characters
          let gradeName = 'Polymer';
          const codeUpper = String(code).toUpperCase();
          
          if (codeUpper.startsWith('S') || codeUpper.startsWith('DG') || codeUpper.startsWith('TL')) {
            gradeName = 'PVC';
          } else if (codeUpper.startsWith('H') || codeUpper.startsWith('HD')) {
            gradeName = 'HDPE';
          } else if (codeUpper.startsWith('L') || codeUpper.startsWith('LD')) {
            gradeName = 'LDPE';
          } else if (codeUpper.startsWith('LL')) {
            gradeName = 'LLDPE';
          } else if (codeUpper.startsWith('P') || codeUpper.startsWith('PP')) {
            gradeName = 'PP';
          }

          matchedGrade = {
            code_name: code,
            grade_name: gradeName,
            brand_name: codeUpper.includes('LG') ? 'LG' : 
                        codeUpper.includes('FORMOSA') ? 'Formosa' : 
                        codeUpper.includes('DAGU') ? 'Tianjin Dagu' : 'Prime Polymer',
            process_name: 'Moulding & Extrusion',
            mfi_value: 'Typical Standard'
          };
        }

        setGradeItem(matchedGrade);
        
        // Map basic grade to full TDS structural specification details
        const structuredDetails = getTdsDetails(matchedGrade);
        setTdsData(structuredDetails);

        // Dynamic SEO Optimization
        document.title = `TDS Technical Data Sheet - ${structuredDetails.polymerFamily} ${structuredDetails.gradeCode} | BSB Fortrade`;
        
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
          metaDesc = document.createElement('meta');
          metaDesc.name = "description";
          document.head.appendChild(metaDesc);
        }
        metaDesc.content = `Get typical physical, mechanical, thermal, and processing properties for ${structuredDetails.brandName} ${structuredDetails.gradeCode} ${structuredDetails.gradeName} (${structuredDetails.polymerFamily}). Food contact compliance, regulatory guidelines, and PDF datasheet download.`;

      } catch (err) {
        console.error('Failed to load TDS specifications:', err);
        setError('Failed to load technical specifications. Please check the grade code.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [code]);

  // Handle auto-download/print trigger if page was opened with ?download=true
  useEffect(() => {
    if (!loading && tdsData && searchParams.get('download') === 'true') {
      // Small timeout to guarantee DOM is rendered before print dialog opens
      const timer = setTimeout(() => {
        window.print();
        
        // Clean up the URL query parameter after printing
        navigate(`/tds/${code}`, { replace: true });
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [loading, tdsData, searchParams, code, navigate]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="tds-container">
        <div className="tds-loading" style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Generating Technical Data Sheet...</h3>
          <p>Compiling properties, molecular test standards, and compliance certificates.</p>
        </div>
      </div>
    );
  }

  if (error || !tdsData) {
    return (
      <div className="tds-container">
        <div className="tds-empty-state" style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <i className="fas fa-circle-exclamation" style={{ color: '#ef4444' }}></i>
          <h3>Error Loading Specifications</h3>
          <p>{error || 'The requested polymer datasheet could not be found or generated.'}</p>
          <button onClick={() => navigate('/tds')} className="tds-filter-btn active" style={{ marginTop: '20px', display: 'inline-block', alignSelf: 'center' }}>
            <i className="fas fa-arrow-left"></i> Back to Catalog
          </button>
        </div>
      </div>
    );
  }

  const currentDateString = new Date().toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="tds-container">
      {/* Back & Download actions */}
      <div className="tds-detail-wrapper">
        {/* PDF / Corporate Header layout */}
        <div className="tds-detail-header">
          <div className="tds-header-text">
            <h1 className="d-print-none">
              <i className="fas fa-file-pdf text-danger"></i>
              TDS - {tdsData.gradeCode}
            </h1>
            <h1 className="d-none d-print-block" style={{ fontSize: '24px', letterSpacing: '1px' }}>
              BSB FORTRADE — TECHNICAL DATA SHEET
            </h1>
            <p>
              <strong>Grade:</strong> {tdsData.gradeName} | <strong>Manufacturer:</strong> {tdsData.brandName} | <strong>Process:</strong> {tdsData.processName}
            </p>
          </div>
          <div className="tds-header-actions d-print-none">
            <button onClick={() => navigate('/tds')} className="tds-btn-action btn-back">
              <i className="fas fa-arrow-left"></i> Directory
            </button>
            <button onClick={handlePrint} className="tds-btn-action btn-download-pdf">
              <i className="fas fa-print"></i> Save / Print PDF
            </button>
          </div>
        </div>

        {/* Core Specs Body */}
        <div className="tds-detail-body">
          {/* Header Metadata block for print version only */}
          <div className="d-none d-print-block" style={{ borderBottom: '2.5px solid #0f172a', paddingBottom: '16px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: '0', fontSize: '20px', color: '#0f172a' }}>{tdsData.polymerFamily} — {tdsData.gradeCode}</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#475569' }}>
                  Suspension Polymer Grade formulated by {tdsData.brandName}
                </p>
              </div>
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#475569' }}>
                <p style={{ margin: 0 }}><strong>Document ID:</strong> BSB-TDS-{tdsData.gradeCode}</p>
                <p style={{ margin: '2px 0 0 0' }}><strong>Issued Date:</strong> {currentDateString}</p>
              </div>
            </div>
          </div>

          {/* Description Card */}
          <div className="tds-section">
            <div className="tds-desc-card">
              {tdsData.description}
            </div>
          </div>

          {/* Typical Properties Table */}
          <div className="tds-section">
            <h2>Typical Properties & Material Specifications</h2>
            <table className="tds-spec-table">
              <thead>
                <tr>
                  <th>Typical Property</th>
                  <th>Test Method</th>
                  <th>Nominal Value</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {tdsData.properties.map((prop, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: '600' }}>{prop.name}</td>
                    <td style={{ fontStyle: 'italic', color: '#475569' }}>{prop.testMethod}</td>
                    <td style={{ fontWeight: '700' }}>{prop.value}</td>
                    <td>{prop.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Grid: Applications and Processing Guide */}
          <div className="tds-detail-grid">
            {/* Left Col: Applications */}
            <div className="tds-section">
              <h2>Key Features & Applications</h2>
              <ul className="tds-list">
                {tdsData.applications.map((app, idx) => (
                  <li key={idx}>
                    <i className="fas fa-check text-success" style={{ marginRight: '10px', fontSize: '12px' }}></i>
                    {app}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Col: Temperature Zones / Processing guide */}
            <div className="tds-section">
              <h2>Recommended Processing Conditions</h2>
              <table className="tds-spec-table">
                <thead>
                  <tr>
                    <th>Barrel Section / Zone</th>
                    <th>Temperature Range</th>
                  </tr>
                </thead>
                <tbody>
                  {tdsData.processingGuide.map((guide, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: '500' }}>{guide.zone}</td>
                      <td style={{ fontWeight: '700', color: 'var(--tds-secondary)' }}>{guide.range}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Regulatory & Safety Compliance */}
          <div className="tds-section">
            <h2>Regulatory & Safety Compliance</h2>
            <div className="tds-compliance-grid">
              <div className="tds-compliance-item">
                <i className="fas fa-shield-halved"></i>
                <span>{tdsData.compliance.fda}</span>
              </div>
              <div className="tds-compliance-item">
                <i className="fas fa-check-double"></i>
                <span>{tdsData.compliance.rohs}</span>
              </div>
              <div className="tds-compliance-item">
                <i className="fas fa-circle-nodes"></i>
                <span>{tdsData.compliance.reach}</span>
              </div>
              <div className="tds-compliance-item">
                <i className="fas fa-leaf"></i>
                <span>{tdsData.compliance.bpa}</span>
              </div>
            </div>
          </div>

          {/* Storage & Handling advice */}
          <div className="tds-detail-grid" style={{ marginTop: '20px' }}>
            <div className="tds-section">
              <h2 style={{ fontSize: '14px', borderBottom: '1px solid #e2e8f0' }}>Storage Recommendations</h2>
              <p style={{ fontSize: '13px', lineHeight: '1.5', color: '#475569' }}>
                {tdsData.handling.storage}
              </p>
            </div>
            <div className="tds-section">
              <h2 style={{ fontSize: '14px', borderBottom: '1px solid #e2e8f0' }}>Safe Handling</h2>
              <p style={{ fontSize: '13px', lineHeight: '1.5', color: '#475569' }}>
                {tdsData.handling.safety}
              </p>
            </div>
          </div>

          {/* Technical Disclaimer */}
          <div className="tds-sheet-footer">
            <p>
              <strong>Disclaimer:</strong> The technical information, suggestions, and recommendations presented in this Technical Data Sheet (TDS) are, to the best of BSB Fortrade knowledge, accurate and reliable as of the date of publication. Since the actual processing and application conditions are completely beyond our direct control, BSB Fortrade makes no warranty or representation, express or implied, regarding the suitability or performance of these products for any specific commercial application. It is the sole responsibility of the customer / industrial buyer to perform adequate testing to verify the suitability of the material.
            </p>
            <p style={{ textAlign: 'center', marginTop: '15px', fontWeight: '600' }}>
              BSB Fortrade Pvt. Ltd. | www.bsbfortrade.com | support@bsbfortrade.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TdsDetailPage;
